import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslations } from 'use-intl';
import { useLanguage, enabledLanguages, isMultiLingual } from 'i18n';

/**
 * `variant` selects the wrapper's positioning only — the control itself is
 * identical everywhere.
 *
 * - `header` (default): the original Header placement, which relies on the
 *   Header's flex/grid context (`margin-left: auto`, and a grid column at
 *   ≥1440px).
 * - `fixed`: pinned to the top-right of the viewport, for the screens that
 *   render no Header — every pre-auth surface, and `/` even when signed in
 *   (§7.4 follow-up). Pre-auth there is no signed-in user, so `setLanguage`
 *   writes only the localStorage mirror — which is exactly the value the
 *   magic-link send passes as `data.language`.
 */
function LanguageSwitcher({ variant = 'header' }) {
  const t = useTranslations('Header');
  const { language, setLanguage } = useLanguage();
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = () => {
      if (showMenu) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMenu]);

  // Single-language instances never show a switcher (spec §5.2). Build-time
  // constant, so the early return can't change hook order between renders.
  if (!isMultiLingual) {
    return null;
  }

  const activeLanguage = enabledLanguages.find((l) => l.code === language);

  const handleSelect = (code) => {
    setLanguage(code);
    setShowMenu(false);
  };

  return (
    <SwitcherWrapper $variant={variant}>
      <SwitcherButton
        type="button"
        aria-label={t('changeLanguage')}
        aria-haspopup="listbox"
        aria-expanded={showMenu}
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}>
        {activeLanguage?.nativeName ?? language}
        <DropdownArrow $isOpen={showMenu}>▼</DropdownArrow>
      </SwitcherButton>
      {showMenu && (
        <SwitcherMenu onClick={(e) => e.stopPropagation()}>
          {enabledLanguages.map((l) => (
            <SwitcherItem
              key={l.code}
              type="button"
              lang={l.code}
              $isActive={l.code === language}
              onClick={() => handleSelect(l.code)}>
              {l.nativeName}
            </SwitcherItem>
          ))}
        </SwitcherMenu>
      )}
    </SwitcherWrapper>
  );
}

// The fixed variant must clear the Dialog overlay, which sits at z-index 4 —
// otherwise the picker is painted behind the scrim on any screen showing a
// login dialog, which is exactly where it is needed.
const SwitcherWrapper = styled.div`
  ${({ $variant }) =>
    $variant === 'fixed'
      ? css`
          position: fixed;
          right: 0.75rem;
          top: 0.75rem;
          z-index: 5;
        `
      : css`
          /* The Header's Container is a flex row with no gap, and margin-left:
             auto eats the free space, so justify-content: space-between adds
             nothing here — without an explicit margin the switcher sits flush
             against the avatar pill. From 768px HeaderButtonContainer's own
             margin-left provides the separation instead. */
          margin-left: auto;
          margin-right: 0.5rem;
          position: relative;
          z-index: 4;

          @media (min-width: 768px) {
            margin-right: 0;
          }

          @media (min-width: 1440px) {
            /* A relative nudge rather than a negative margin: the wrapper is
               already position: relative, so this shifts the button and its
               absolutely-positioned menu together without affecting the grid
               track or the siblings' layout. */
            grid-column: 9/10;
            justify-self: end;
            left: 1.25rem;
            margin-left: 0;
            margin-right: 0;
          }
        `}
`;

const SwitcherButton = styled.button`
  align-items: center;
  background-color: rgba(0, 123, 255, 0.7);
  border: none;
  border-radius: var(--border-radius-lg);
  color: #fff;
  cursor: pointer;
  display: flex;
  font-family: 'Poppins', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  gap: 0.5rem;
  justify-content: space-between;
  outline: none;
  padding: 0.625rem 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    filter: brightness(1.225);
    outline: 0.063em solid rgba(0, 123, 255, 0.7);
    outline-offset: 0.125em;
  }

  &:active {
    transform: scale(0.97);
  }

  @media (min-width: 1000px) {
    font-size: 1rem;
  }
`;

const DropdownArrow = styled.span`
  font-size: 0.625rem;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease-in-out;
`;

const SwitcherMenu = styled.div`
  background-color: #fff;
  border: 1px solid rgba(0, 123, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 0.5rem;
  min-width: 100%;
  position: absolute;
  right: 0;
  z-index: 5;
`;

const SwitcherItem = styled.button`
  background-color: ${({ $isActive }) =>
    $isActive ? 'rgba(0, 123, 255, 0.1)' : 'transparent'};
  border: none;
  color: var(--blue);
  cursor: pointer;
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 0.875rem;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '400')};
  outline: none;
  padding: 0.75rem 1rem;
  text-align: left;
  transition: background-color 0.2s ease-in-out;
  white-space: nowrap;
  width: 100%;

  &:hover {
    background-color: rgba(0, 123, 255, 0.15);
  }

  &:active {
    background-color: rgba(0, 123, 255, 0.25);
  }

  @media (min-width: 1000px) {
    font-size: 1rem;
  }
`;

export default LanguageSwitcher;
